import {
  Button,
  Center,
  Code,
  Loader,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconInbox, IconLock } from "@tabler/icons-react";
import type { ReactNode } from "react";

export interface LoadingStateProps {
  /** Rows of skeleton to draw once the layout is known; a spinner otherwise. */
  readonly rows?: number;
  readonly label?: string;
}
export function LoadingState({ rows, label = "Loading" }: LoadingStateProps) {
  if (rows !== undefined && rows > 0) {
    return (
      <Stack gap="sm" role="status" aria-label={label} data-testid="loading-skeleton">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} height={40} radius="md" />
        ))}
      </Stack>
    );
  }
  return (
    <Center py="xl" role="status" aria-label={label}>
      <Loader />
    </Center>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly icon?: ReactNode;
}
export function EmptyState({ title, description, action, icon }: EmptyStateProps) {
  return (
    <Center py="xl">
      <Stack align="center" gap="xs" maw={420} ta="center">
        <ThemeIcon
          variant="light"
          color="gray"
          size={52}
          radius="xl"
          className="how-state-icon"
          aria-hidden
        >
          {icon ?? <IconInbox size={22} />}
        </ThemeIcon>
        <Title order={3}>{title}</Title>
        {description !== undefined && (
          <Text c="dimmed" size="sm">
            {description}
          </Text>
        )}
        {action}
      </Stack>
    </Center>
  );
}

export interface ErrorStateProps {
  readonly title?: string;
  /** Safe, user-facing message; never raw error internals. */
  readonly message?: string;
  readonly onRetry?: () => void;
  /**
   * The error reference to quote to support (ADR 0038), shown in a fixed-width face so it can be read aloud or
   * copied exactly. Present only when the failure was reported.
   */
  readonly reference?: string;
}
export function ErrorState({
  title = "Something went wrong",
  message = "Please try again.",
  onRetry,
  reference,
}: ErrorStateProps) {
  return (
    <Center py="xl" role="alert">
      <Stack align="center" gap="xs" maw={420} ta="center">
        <ThemeIcon
          variant="light"
          color="red"
          size={52}
          radius="xl"
          className="how-state-icon"
          aria-hidden
        >
          <IconAlertTriangle size={22} />
        </ThemeIcon>
        <Title order={3}>{title}</Title>
        <Text c="dimmed" size="sm">
          {message}
        </Text>
        {reference !== undefined && (
          <Text size="sm">
            Reference <Code fz="sm">{reference}</Code>
          </Text>
        )}
        {onRetry !== undefined && (
          <Button variant="light" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Stack>
    </Center>
  );
}

export interface PermissionDeniedProps {
  readonly title?: string;
  readonly message?: string;
  readonly action?: ReactNode;
}
export function PermissionDenied({
  title = "You do not have access",
  message = "Ask a tenant administrator if you need this permission.",
  action,
}: PermissionDeniedProps) {
  return (
    <Center py="xl" role="alert">
      <Stack align="center" gap="xs" maw={420} ta="center">
        <ThemeIcon variant="light" color="yellow" size="xl" radius="xl" aria-hidden>
          <IconLock size={22} />
        </ThemeIcon>
        <Title order={3}>{title}</Title>
        <Text c="dimmed" size="sm">
          {message}
        </Text>
        {action}
      </Stack>
    </Center>
  );
}
